import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DetailedHTMLProps, ImgHTMLAttributes, SyntheticEvent, useCallback, useMemo } from 'react';
import { generateImageUrls } from 'utils/generateImageUrl';
import { Transformation } from '@diritto/common/imageKit/transformation';

interface StyleProps {
  fullWidth?: boolean;
}

interface TransformProps {
  transformation?: Transformation;
  path: string;
  isPrivate: boolean;
}

type ImageProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> &
  StyleProps & {
    transform?: TransformProps;
  };

export const IkImage = ({
  src,
  className,
  alt,
  onError,
  fullWidth = false,
  transform,
  onLoad,
  ...extraProps
}: ImageProps) => {
  const transformUrls = useMemo(() => {
    if (!transform) {
      return undefined;
    }

    const { isPrivate, path, transformation } = transform;
    return generateImageUrls({
      path,
      isPrivate,
      transformation: transformation ?? {},
    });
  }, [transform]);

  const sourceUrl = useMemo(() => src || transformUrls?.jpg, [src, transformUrls]);

  const handleError = useCallback(
    (e: SyntheticEvent<HTMLImageElement, Event>) => {
      (e.target as HTMLImageElement).onerror = null;
      onError?.(e);
    },
    [onError],
  );

  const handleLoaded = useCallback((e: SyntheticEvent<HTMLImageElement, Event>) => onLoad?.(e), [onLoad]);

  if (transformUrls) {
    return (
      <StyledPicture>
        <source srcSet={transformUrls.webp} type="image/webp" />
        <source srcSet={transformUrls.jpg} type="image/jpeg" />
        <StyledImage
          suppressHydrationWarning
          fullwidth={fullWidth}
          src={transformUrls.jpg}
          onError={handleError}
          onLoad={handleLoaded}
          loading="lazy"
          className={className}
          {...extraProps}
        />
      </StyledPicture>
    );
  }

  return (
    <StyledImage
      suppressHydrationWarning
      fullwidth={fullWidth}
      src={sourceUrl}
      onError={handleError}
      loading="lazy"
      className={className}
      {...extraProps}
    />
  );
};

const StyledPicture = styled.picture`
  display: flex;
  height: 100%;
  width: 100%;
`;

const StyledImage = styled.img(
  ({ fullwidth }: { fullwidth: boolean }) => css`
    width: ${fullwidth ? '100%' : 'auto'};
  `,
);
